import { useMediaQuery } from "@mui/material";
import * as Sentry from "@sentry/react";
import { ErrorBoundary } from "@sentry/react";
import { useAppSelector } from "app/hooks";
import { AddAchievement } from "components/Layout/Achievement/AchievementForm/AddAchievement";
import { EditAchievement } from "components/Layout/Achievement/AchievementForm/EditAchievement";
import { AchievementImportList } from "components/Layout/Achievement/AchievementImportList/AchievementsImportList";
import { EditImportedAchievement } from "components/Layout/Achievement/AchievementImportList/EditImportedAchievement";
import { AchievementList } from "components/Layout/Achievement/AchievementList/AchievementList";
import { AchievementTemplates } from "components/Layout/Achievement/AchievementTemplates/AchievementTemplates";
import { SingleAchievement } from "components/Layout/Achievement/SingleAchievement/SingleAchievement";
import { ActionDetails } from "components/Layout/Actions/ActionDetails";
import { AdminData } from "components/Layout/Admin/AdminData";
import { AddAdmin } from "components/Layout/Admin/AdminForm/AddAdmin";
import { EditAdmin } from "components/Layout/Admin/AdminForm/EditAdmin";
import { AddAutomation } from "components/Layout/Campaign/Automations/AutomationForm/AddAutomation";
import { EditAutomation } from "components/Layout/Campaign/Automations/AutomationForm/EditAutomation";
import { AutomationsImportList } from "components/Layout/Campaign/Automations/AutomationsImportList/AutomationsImportList";
import { EditImportedAutomation } from "components/Layout/Campaign/Automations/AutomationsImportList/EditImportedAutomation";
import { AutomationsList } from "components/Layout/Campaign/Automations/AutomationsList";
import { SingleAutomation } from "components/Layout/Campaign/Automations/SingleAutomation/SingleAutomation";
import { SimulateCampaign } from "components/Layout/Campaign/CampaignSimulation/SimulateCampaign";
import { AddCampaign } from "components/Layout/Campaign/Direct/CampaignForm/AddCampaign";
import { EditCampaign } from "components/Layout/Campaign/Direct/CampaignForm/EditCampaign";
import { CampaignImportList } from "components/Layout/Campaign/Direct/CampaignImportList/CampaignImportList";
import { EditImportedCampaign } from "components/Layout/Campaign/Direct/CampaignImportList/EditImportedCampaign";
import { CampaignList } from "components/Layout/Campaign/Direct/CampaignList/CampaignList";
import { SingleCampaign } from "components/Layout/Campaign/Direct/CampaignList/SingleCampaign/SingleCampaign";
import { CampaignRedemptionCodeList } from "components/Layout/Campaign/Direct/CampaignRedemptionCodes/CampaignRedemptionCodeList";
import { AddReferral } from "components/Layout/Campaign/Referrals/ReferralsForm/AddReferral";
import { EditReferral } from "components/Layout/Campaign/Referrals/ReferralsForm/EditReferral";
import { EditImportedReferral } from "components/Layout/Campaign/Referrals/ReferralsImportList/EditImportedReferral";
import { ReferralsImportList } from "components/Layout/Campaign/Referrals/ReferralsImportList/ReferralsImportList";
import ReferralsList from "components/Layout/Campaign/Referrals/ReferralsList";
import { SingleReferral } from "components/Layout/Campaign/Referrals/SingleReferral/SingleReferral";
import { ChangePassword } from "components/Layout/ChangePassword";
import { AddChannel } from "components/Layout/Channel/ChannelForm/AddChannel";
import { EditChannel } from "components/Layout/Channel/ChannelForm/EditChannel";
import { ChannelList } from "components/Layout/Channel/ChannelList/ChannelList";
import { AddCollection } from "components/Layout/Collection/CollectionForm/AddCollection";
import { EditCollection } from "components/Layout/Collection/CollectionForm/EditCollection";
import { CollectionsList } from "components/Layout/Collection/CollectionsList/CollectionsList";
import { SingleCollection } from "components/Layout/Collection/SingleCollection/SingleCollection";
import { Communication } from "components/Layout/Communication";
import { CustomEventsList } from "components/Layout/CustomEvent/EventList/CustomEventsList";
import { AddCustomEventSchema } from "components/Layout/CustomEvent/SchemaForm/AddCustomEventSchema";
import { EditCustomEventSchema } from "components/Layout/CustomEvent/SchemaForm/EditCustomEventSchema";
import { CustomEventSchemaImportList } from "components/Layout/CustomEvent/SchemaImportList/CustomEventSchemaImportList";
import { EditImportedCustomEventSchema } from "components/Layout/CustomEvent/SchemaImportList/EditImportedCustomEventSchema";
import { CustomEventsSchemaList } from "components/Layout/CustomEvent/SchemaList/CustomEventsSchemaList";
import { SingleCustomEvent } from "components/Layout/CustomEvent/SingleEvent/SingleCustomEvent";
import { Dashboard } from "components/Layout/Dashboard";
import { ErrorFallback } from "components/Layout/ErrorFallback";
import { ErrorPage } from "components/Layout/ErrorPage";
import { FormLayout } from "components/Layout/FormLayout";
import { Analytics } from "components/Layout/GlobalManagement/Analytics/Analytics";
import { ConfigDuplication } from "components/Layout/GlobalManagement/ConfigDuplication/ConfigDuplication";
import { ConfigImportList } from "components/Layout/GlobalManagement/ConfigDuplication/ConfigImportList";
import { Settings } from "components/Layout/GlobalManagement/Settings/Settings";
import { SystemLogsList } from "components/Layout/GlobalManagement/Settings/SystemLogsList";
import { UsageChart } from "components/Layout/GlobalManagement/Usage/UsageChart";
import { CouponsImportDetails } from "components/Layout/Import/ImportDetails/CouponsImportDetails";
import { GroupsOfValuesImportDetails } from "components/Layout/Import/ImportDetails/GroupsOfValuesImportDetails";
import { MembersImportDetails } from "components/Layout/Import/ImportDetails/MembersImportDetails";
import { TransactionImportDetails } from "components/Layout/Import/ImportDetails/TransactionImportDetails";
import { UnitsTransfersImportDetails } from "components/Layout/Import/ImportDetails/UnitsTransfersImportDetails";
import { ImportsExports } from "components/Layout/ImportsExports/ImportsExports";
import { AddLanguage } from "components/Layout/Language/LanguageForm/AddLanguage";
import { EditLanguage } from "components/Layout/Language/LanguageForm/EditLanguage";
import { EditTranslation } from "components/Layout/Language/LanguageForm/EditTranslation";
import { LayoutComponent } from "components/Layout/LayoutComponent";
import { AddLeaderboard } from "components/Layout/Leaderboards/LeaderboardForm/AddLeaderboard";
import { EditLeaderboard } from "components/Layout/Leaderboards/LeaderboardForm/EditLeaderboard";
import { LeaderboardsList } from "components/Layout/Leaderboards/LeaderboardList/LeaderboardList";
import SingleLeaderboard from "components/Layout/Leaderboards/SingleLeaderboard/SingleLeaderboard";
import { LoginForm } from "components/Layout/LoginForm";
import { AddMember } from "components/Layout/Member/MemberForm/AddMember";
import { EditMember } from "components/Layout/Member/MemberForm/EditMember";
import { MemberList } from "components/Layout/Member/MemberList/MemberList";
import { MembersConfiguration } from "components/Layout/Member/MembersConfiguration/MembersConfiguration";
import { Achievements } from "components/Layout/Member/SingleMember/Achievements";
import { Badges as MemberBadges } from "components/Layout/Member/SingleMember/Badges/Badges";
import { Campaigns } from "components/Layout/Member/SingleMember/Campaigns";
import { CustomEvents } from "components/Layout/Member/SingleMember/CustomEvents";
import { GeneralInfo } from "components/Layout/Member/SingleMember/GeneralInfo";
import { Leaderboards } from "components/Layout/Member/SingleMember/Leaderboards/Leaderboards";
import { Referrals } from "components/Layout/Member/SingleMember/Referrals";
import { Rewards } from "components/Layout/Member/SingleMember/Rewards";
import { SingleMemberParent } from "components/Layout/Member/SingleMember/SingleMemberParent";
import { Tiers } from "components/Layout/Member/SingleMember/Tiers";
import { Timeline } from "components/Layout/Member/SingleMember/Timeline";
import { Transactions } from "components/Layout/Member/SingleMember/Transactions";
import { Wallets } from "components/Layout/Member/SingleMember/Wallets";
import { PasswordRecovery } from "components/Layout/PasswordRecovery";
import { PasswordReset } from "components/Layout/PasswordReset";
import { ReferredMembersList } from "components/Layout/ReferredMembersList";
import { ListOfRewardCategories } from "components/Layout/Reward/RewardCategoriesList/ListOfRewardCategories";
import { RewardConfiguration } from "components/Layout/Reward/RewardConfiguration/RewardConfiguration";
import { AddReward } from "components/Layout/Reward/RewardForm/AddReward";
import { EditReward } from "components/Layout/Reward/RewardForm/EditReward";
import { RewardFulfillmentList } from "components/Layout/Reward/RewardFulfillmentList/RewardFulfillmentList";
import { EditImportedReward } from "components/Layout/Reward/RewardImportList/EditImportedReward";
import { RewardImportList } from "components/Layout/Reward/RewardImportList/RewardImportList";
import { RewardList } from "components/Layout/Reward/RewardList/RewardList";
import { SingleReward } from "components/Layout/Reward/SingleReward/SingleReward";
import { SingleRewardFulfillment } from "components/Layout/Reward/SingleRewardFulfillment/SingleRewardFulfillment";
import { AddRole } from "components/Layout/Role/RoleForm/AddRole";
import { EditRole } from "components/Layout/Role/RoleForm/EditRole";
import { AddSegment } from "components/Layout/Segment/SegmentForm/AddSegment";
import { EditSegment } from "components/Layout/Segment/SegmentForm/EditSegment";
import { DeactivatedSegmentReview } from "components/Layout/Segment/SegmentList/DeactivatedSegmentReview";
import { SegmentList } from "components/Layout/Segment/SegmentList/SegmentList";
import { SingleSegment } from "components/Layout/Segment/SingleSegment/SingleSegment";
import { AddStore } from "components/Layout/Store/StoreForm/AddStore";
import { EditStore } from "components/Layout/Store/StoreForm/EditStore";
import { TierConfiguration } from "components/Layout/Tier/TierConfiguration/TierConfiguration";
import { AddTier } from "components/Layout/Tier/TierForm/AddTier";
import { EditTier } from "components/Layout/Tier/TierForm/EditTier";
import { TierList } from "components/Layout/Tier/TierList/TierList";
import { TierSet } from "components/Layout/Tier/TierSet/TierSet";
import { AddTierSet } from "components/Layout/Tier/TierSet/TierSetForm/AddTierSet";
import { EditTierSet } from "components/Layout/Tier/TierSet/TierSetForm/EditTierSet";
import { AddTiers } from "components/Layout/Tier/TierSet/TiersForm/AddTiers";
import { EditTiers } from "components/Layout/Tier/TierSet/TiersForm/EditTiers";
import { TierSetList } from "components/Layout/Tier/TierSetList/TierSetList";
import { SingleTransaction } from "components/Layout/Transaction/SingleTransaction/SingleTransaction";
import { AddTransaction } from "components/Layout/Transaction/TransactionForm/AddTransaction";
import { EditTransaction } from "components/Layout/Transaction/TransactionForm/EditTransaction";
import { TransactionList } from "components/Layout/Transaction/TransactionList/TransactionList";
import { UnitTransfersList } from "components/Layout/Unit/UnitTransfersList/UnitTransfersList";
import { UnsupportedDevice } from "components/Layout/UnsupportedDevice";
import { SingleWallet } from "components/Layout/WalletType/SingleWallet/SingleWallet";
import { AddWalletType } from "components/Layout/WalletType/WalletTypeForm/AddWalletType";
import { EditWalletType } from "components/Layout/WalletType/WalletTypeForm/EditWalletType";
import { EditImportedWalletType } from "components/Layout/WalletType/WalletTypeImportList/EditImportedWalletType";
import { WalletTypeImportList } from "components/Layout/WalletType/WalletTypeImportList/WalletTypeImportList";
import { WalletTypeList } from "components/Layout/WalletType/WalletTypeList/WalletTypeList";
import { AddWebhook } from "components/Layout/Webhook/WebhookForm/AddWebhook";
import { EditWebhook } from "components/Layout/Webhook/WebhookForm/EditWebhook";
import { WebhookList } from "components/Layout/Webhook/WebhookList/WebhookList";
import { InformationProvider } from "contexts/Informations/InformationProvider";
import { mediaQuery } from "contexts/Theme/ThemeProvider";
import { useAuth } from "hooks/auth";
import { LayoutInterceptor } from "LayoutInterceptor";
import LoginCallbackPage from "LoginCallbackPage";
import React from "react";
import {
  createBrowserRouter,
  Navigate,
  Route,
  Routes as RouterRoutes,
} from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { ImportTypes } from "types/import";
import { links } from "utilities/links";

export function Routes() {
  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <RouterRoutes>
        <Route
          element={
            <RouteWithLayout component={LoginForm} public layout={FormLayout} />
          }
          path={links.homepage}
        />
        <Route
          element={
            <RouteWithLayout
              component={LoginCallbackPage}
              layout={FormLayout}
              public
            />
          }
          path={links.oktaLoginCallback}
        />
        <Route
          element={
            <RouteWithLayout
              component={PasswordRecovery}
              public
              layout={FormLayout}
            />
          }
          path={links.passwordRecovery}
        />
        <Route
          element={
            <RouteWithLayout
              component={PasswordReset}
              public
              layout={FormLayout}
            />
          }
          path={links.passwordReset()}
        />
        <Route
          element={<RouteWithLayout component={Dashboard} />}
          path={links.dashboard}
        />
        {/* Members */}
        <Route
          element={<RouteWithLayout component={MemberList} />}
          path={links.membersList}
        />
        <Route
          element={<RouteWithLayout component={AddMember} />}
          path={links.addMember}
        />
        <Route
          element={<RouteWithLayout component={EditMember} />}
          path={links.editMember()}
        />
        {/* new single member pages */}
        <Route element={<RouteWithLayout component={SingleMemberParent} />}>
          <Route
            element={<GeneralInfo />}
            path={links.singleMemberGeneralInfo()}
          />
          <Route
            element={<Wallets fullView />}
            path={links.singleMemberWallets()}
          />
          <Route element={<Timeline />} path={links.singleMemberTimeline()} />
          <Route element={<Tiers />} path={links.singleMemberTiers()} />
          <Route
            element={<Transactions />}
            path={links.singleMemberTransactions()}
          />
          <Route
            element={<CustomEvents />}
            path={links.singleMemberCustomEvents()}
          />
          <Route element={<Campaigns />} path={links.singleMemberCampaigns()} />
          <Route
            element={<Achievements />}
            path={links.singleMemberAchievements()}
          />
          <Route element={<Rewards />} path={links.singleMemberRewards()} />
          <Route element={<Referrals />} path={links.singleMemberReferrals()} />
          <Route element={<MemberBadges />} path={links.singleMemberBadges()} />
          <Route
            element={<Leaderboards />}
            path={links.singleMemberLeaderboards()}
          />
        </Route>
        {/* end of new single member pages */}
        <Route
          element={<RouteWithLayout component={MembersConfiguration} />}
          path={links.membersConfiguration}
        />
        {/* Segments */}
        <Route
          element={<RouteWithLayout component={SegmentList} />}
          path={links.segmentsList}
        />
        <Route
          element={<RouteWithLayout component={AddSegment} />}
          path={links.addSegment}
        />
        <Route
          element={<RouteWithLayout component={EditSegment} />}
          path={links.editSegment()}
        />
        <Route
          element={<RouteWithLayout component={SingleSegment} />}
          path={links.segment()}
        />
        <Route
          element={<RouteWithLayout component={DeactivatedSegmentReview} />}
          path={links.deactivatedSegment()}
        />
        {/* Transactions */}
        <Route
          element={<RouteWithLayout component={TransactionList} />}
          path={links.transactionsList}
        />
        <Route
          element={<RouteWithLayout component={AddTransaction} />}
          path={links.addTransaction}
        />
        <Route
          element={<RouteWithLayout component={EditTransaction} />}
          path={links.editTransaction()}
        />
        <Route
          element={<RouteWithLayout component={SingleTransaction} />}
          path={links.transaction()}
        />
        {/* Custom events */}
        <Route
          element={<RouteWithLayout component={CustomEventsList} />}
          path={links.customEventsList}
        />
        <Route
          element={<RouteWithLayout component={ReferredMembersList} />}
          path={links.referredMembersList}
        />
        <Route
          element={<RouteWithLayout component={SingleCustomEvent} />}
          path={links.customEvent()}
        />
        <Route
          element={<RouteWithLayout component={CustomEventsSchemaList} />}
          path={links.customEventsSchemaList}
        />
        <Route
          element={<RouteWithLayout component={AddCustomEventSchema} />}
          path={links.addCustomEventSchema}
        />
        <Route
          element={<RouteWithLayout component={EditCustomEventSchema} />}
          path={links.editCustomEventSchema()}
        />
        <Route
          element={<RouteWithLayout component={CustomEventSchemaImportList} />}
          path={links.customEventSchemaImportList}
        />
        <Route
          element={
            <RouteWithLayout component={EditImportedCustomEventSchema} />
          }
          path={links.editImportedCustomEventSchema}
        />
        {/* Campaigns */}
        <Route
          element={<RouteWithLayout component={CampaignList} />}
          path={links.campaignsList}
        />
        <Route
          element={<RouteWithLayout component={AddCampaign} />}
          path={links.addCampaign}
        />
        <Route
          element={<RouteWithLayout component={SimulateCampaign} />}
          path={links.simulateCampaign}
        />
        <Route
          element={<RouteWithLayout component={EditCampaign} />}
          path={links.editCampaign()}
        />
        <Route
          element={<RouteWithLayout component={CampaignRedemptionCodeList} />}
          path={links.redemptionCodeList()}
        />
        <Route
          element={<RouteWithLayout component={SingleCampaign} />}
          path={links.campaign()}
        />
        <Route
          element={<RouteWithLayout component={CampaignImportList} />}
          path={links.campaignsImportList}
        />
        <Route
          element={<RouteWithLayout component={EditImportedCampaign} />}
          path={links.editImportedCampaign}
        />
        {/* Referrals */}
        <Route
          element={<RouteWithLayout component={ReferralsList} />}
          path={links.referralsList}
        />
        <Route
          element={<RouteWithLayout component={AddReferral} />}
          path={links.addReferral}
        />
        <Route
          element={<RouteWithLayout component={EditReferral} />}
          path={links.editReferral()}
        />
        <Route
          element={<RouteWithLayout component={SingleReferral} />}
          path={links.referral()}
        />
        <Route
          element={<RouteWithLayout component={ReferralsImportList} />}
          path={links.referralsImportList}
        />
        <Route
          element={<RouteWithLayout component={EditImportedReferral} />}
          path={links.editImportedReferral}
        />
        {/* Tiers */}
        <Route
          element={<RouteWithLayout component={TierList} />}
          path={links.tiersList}
        />
        <Route
          element={<RouteWithLayout component={AddTier} />}
          path={links.addTier}
        />
        <Route
          element={<RouteWithLayout component={EditTier} />}
          path={links.editTier()}
        />
        <Route
          element={<RouteWithLayout component={TierConfiguration} />}
          path={links.tiersConfiguration}
        />
        <Route
          element={<RouteWithLayout component={TierSetList} />}
          path={links.tierSetList}
        />
        <Route
          element={<RouteWithLayout component={AddTierSet} />}
          path={links.addTierSet}
        />
        <Route
          element={<RouteWithLayout component={EditTierSet} />}
          path={links.editTierSet()}
        />
        <Route
          element={<RouteWithLayout component={TierSet} />}
          path={links.tierSet()}
        />
        <Route
          element={<RouteWithLayout component={AddTiers} />}
          path={links.tierSetAddTiers()}
        />
        <Route
          element={<RouteWithLayout component={EditTiers} />}
          path={links.tierSetEditTiers()}
        />
        {/* Achievements */}
        <Route
          element={<RouteWithLayout component={AchievementList} />}
          path={links.achievementsList}
        />
        <Route
          element={<RouteWithLayout component={AchievementImportList} />}
          path={links.achievementsImportList}
        />
        <Route
          element={<RouteWithLayout component={AddAchievement} />}
          path={links.addAchievement}
        />
        <Route
          element={<RouteWithLayout component={EditAchievement} />}
          path={links.editAchievement()}
        />
        <Route
          element={<RouteWithLayout component={SingleAchievement} />}
          path={links.achievement()}
        />
        <Route
          element={<RouteWithLayout component={EditImportedAchievement} />}
          path={links.editImportedAchievement}
        />
        <Route
          element={<RouteWithLayout component={AchievementTemplates} />}
          path={links.achievementTemplates}
        />
        {/* Wallet types */}
        <Route
          element={<RouteWithLayout component={WalletTypeList} />}
          path={links.walletTypesList}
        />
        <Route
          element={<RouteWithLayout component={SingleWallet} />}
          path={links.wallet()}
        />
        <Route
          element={<RouteWithLayout component={AddWalletType} />}
          path={links.addWalletType}
        />
        <Route
          element={<RouteWithLayout component={EditWalletType} />}
          path={links.editWalletType()}
        />
        <Route
          element={<RouteWithLayout component={WalletTypeImportList} />}
          path={links.walletsImportList}
        />
        <Route
          element={<RouteWithLayout component={EditImportedWalletType} />}
          path={links.editImportedWallet}
        />
        <Route
          element={<RouteWithLayout component={UnitTransfersList} />}
          path={links.unitTransfers}
        />
        {/* Rewards */}
        <Route
          element={<RouteWithLayout component={RewardList} />}
          path={links.rewardsList}
        />
        <Route
          element={<RouteWithLayout component={AddReward} />}
          path={links.addReward}
        />
        <Route
          element={<RouteWithLayout component={EditReward} />}
          path={links.editReward()}
        />
        <Route
          element={<RouteWithLayout component={RewardFulfillmentList} />}
          path={links.rewardFulfillmentsList}
        />
        <Route
          element={<RouteWithLayout component={ListOfRewardCategories} />}
          path={links.rewardCategoriesList}
        />
        <Route
          element={<RouteWithLayout component={SingleRewardFulfillment} />}
          path={links.rewardsFulfillment()}
        />
        <Route
          element={<RouteWithLayout component={SingleReward} />}
          path={links.reward()}
        />
        <Route
          element={<RouteWithLayout component={RewardConfiguration} />}
          path={links.rewardsConfiguration}
        />
        <Route
          element={<RouteWithLayout component={RewardImportList} />}
          path={links.rewardsImportList}
        />
        <Route
          element={<RouteWithLayout component={EditImportedReward} />}
          path={links.editImportedReward}
        />
        {/* Channels */}
        <Route
          element={<RouteWithLayout component={ChannelList} />}
          path={links.channelList}
        />
        <Route
          element={<RouteWithLayout component={AddChannel} />}
          path={links.addChannel}
        />
        <Route
          element={<RouteWithLayout component={EditChannel} />}
          path={links.editChannel()}
        />
        {/* Webhooks */}
        <Route
          element={<RouteWithLayout component={WebhookList} />}
          path={links.webhookList}
        />
        <Route
          element={<RouteWithLayout component={AddWebhook} />}
          path={links.addWebhook}
        />
        <Route
          element={<RouteWithLayout component={EditWebhook} />}
          path={links.editWebhook()}
        />
        {/* Groups */}
        <Route
          element={<RouteWithLayout component={CollectionsList} />}
          path={links.collectionsList}
        />
        <Route
          element={<RouteWithLayout component={SingleCollection} />}
          path={links.collection()}
        />
        <Route
          element={<RouteWithLayout component={AddCollection} />}
          path={links.addCollection}
        />
        <Route
          element={<RouteWithLayout component={EditCollection} />}
          path={links.editCollection()}
        />
        {/* Imports Exports */}
        <Route
          element={<RouteWithLayout component={ImportsExports} />}
          path={links.importsExports}
        />
        <Route
          element={<RouteWithLayout component={MembersImportDetails} />}
          path={links.importDetails(ImportTypes.MEMBER)}
        />
        <Route
          element={<RouteWithLayout component={GroupsOfValuesImportDetails} />}
          path={links.importDetails(ImportTypes.GROUP_VALUE)}
        />
        <Route
          element={<RouteWithLayout component={MembersImportDetails} />}
          path={links.importDetails(ImportTypes.SEGMENT_MEMBERS)}
        />
        <Route
          element={<RouteWithLayout component={UnitsTransfersImportDetails} />}
          path={links.importDetails(ImportTypes.UNIT_TRANSFER_ADDING)}
        />
        <Route
          element={<RouteWithLayout component={UnitsTransfersImportDetails} />}
          path={links.importDetails(ImportTypes.UNIT_TRANSFER_SPENDING)}
        />
        <Route
          element={<RouteWithLayout component={TransactionImportDetails} />}
          path={links.importDetails(ImportTypes.TRANSACTION)}
        />
        <Route
          element={<RouteWithLayout component={CouponsImportDetails} />}
          path={links.importDetails(ImportTypes.REWARD_COUPON)}
        />
        {/* Settings */}
        <Route
          element={<RouteWithLayout component={AddStore} />}
          path={links.addStore}
        />
        <Route
          element={<RouteWithLayout component={EditStore} />}
          path={links.editStore()}
        />
        <Route
          element={<RouteWithLayout component={AddRole} />}
          path={links.addRole}
        />
        <Route
          element={<RouteWithLayout component={EditRole} />}
          path={links.editRole()}
        />
        <Route
          element={<RouteWithLayout component={AddAdmin} />}
          path={links.addAdmin}
        />
        <Route
          element={<RouteWithLayout component={EditAdmin} />}
          path={links.editAdmin()}
        />
        <Route
          element={<RouteWithLayout component={AddLanguage} />}
          path={links.addLanguage}
        />
        <Route
          element={<RouteWithLayout component={EditLanguage} />}
          path={links.editLanguage()}
        />
        <Route
          element={<RouteWithLayout component={EditTranslation} />}
          path={links.editTranslation()}
        />
        <Route
          element={<RouteWithLayout component={SystemLogsList} />}
          path={links.systemLogs}
        />
        {/* Global management */}
        <Route
          element={<RouteWithLayout component={Analytics} />}
          path={links.globalAnalytics}
        />
        <Route
          element={<RouteWithLayout component={UsageChart} />}
          path={links.usage}
        />
        <Route
          element={<RouteWithLayout component={Settings} />}
          path={links.settings}
        />
        <Route
          element={<RouteWithLayout component={ConfigDuplication} />}
          path={links.configDuplication}
        />
        <Route
          element={<RouteWithLayout component={ConfigImportList} />}
          path={links.configDuplicationImport}
        />
        {/* Actions */}
        <Route
          element={<RouteWithLayout component={ActionDetails} />}
          path={links.actionDetails()}
        />
        {/* Other */}
        <Route
          element={<RouteWithLayout component={Communication} />}
          path={links.communication}
        />
        <Route
          element={<RouteWithLayout component={ChangePassword} />}
          path={links.changePassword}
        />
        <Route
          element={<RouteWithLayout component={AdminData} />}
          path={links.adminData}
        />
        {/* Automations */}
        <Route
          element={<RouteWithLayout component={AutomationsList} />}
          path={links.automationsList}
        />
        <Route
          element={<RouteWithLayout component={AddAutomation} />}
          path={links.addAutomation}
        />
        <Route
          element={<RouteWithLayout component={EditAutomation} />}
          path={links.editAutomation()}
        />
        <Route
          element={<RouteWithLayout component={AutomationsImportList} />}
          path={links.automationsImportList}
        />
        <Route
          element={<RouteWithLayout component={EditImportedAutomation} />}
          path={links.editImportedAutomation}
        />
        <Route
          element={<RouteWithLayout component={SingleAutomation} />}
          path={links.automation()}
        />
        {/* Leaderboards */}
        <Route
          element={<RouteWithLayout component={LeaderboardsList} />}
          path={links.leaderboardsList}
        />
        <Route
          element={<RouteWithLayout component={SingleLeaderboard} />}
          path={links.leaderboard()}
        />
        <Route
          element={<RouteWithLayout component={AddLeaderboard} />}
          path={links.addLeaderboard}
        />
        <Route
          element={<RouteWithLayout component={EditLeaderboard} />}
          path={links.editLeaderboard()}
        />
        {/* 404 */}
        <Route element={<RouteWithLayout component={ErrorPage} />} path="*" />
      </RouterRoutes>
    </ErrorBoundary>
  );
}
export const router = createBrowserRouter([{ path: "*", Component: Routes }]);
export interface RouteWithLayoutProps {
  component: React.ComponentType;
  public?: boolean;
  layout?: React.ComponentType<any>;
  responsibleView?: boolean;
}

const getContextData = (storeCode: string) => ({
  Tenant: storeCode,
  Language: navigator.language,
});

export const RouteWithLayout = ({
  component: Component,
  layout: Layout = LayoutComponent,
  public: isPublic,
  responsibleView,
}: RouteWithLayoutProps): React.ReactElement => {
  const { hasToken } = useAuth();
  const { code } = useAppSelector(selectCurrentStoreData);
  const desktopWidth = useMediaQuery(`(min-width:${mediaQuery.desktop}px)`);
  const unsupportedDevice = !responsibleView && !desktopWidth;

  if (Sentry.getCurrentHub().getClient()) {
    Sentry.setContext("Additional app settings", getContextData(code));
  }

  if (!isPublic && !hasToken) {
    return <Navigate to={links.homepage} />;
  } else {
    if (isPublic && hasToken) {
      return <Navigate to={links.dashboard} replace={true} />;
    } else {
      return (
        <>
          {unsupportedDevice && <UnsupportedDevice />}
          <div
            style={{
              display: unsupportedDevice ? "none" : "flex",
              minHeight: "100%",
              flexDirection: "column",
            }}
          >
            {hasToken ? (
              <InformationProvider>
                <LayoutInterceptor component={Component} layout={Layout} />
              </InformationProvider>
            ) : (
              <Layout>
                <Component />
              </Layout>
            )}
          </div>
        </>
      );
    }
  }
};
